import { Box, Stack, Typography } from "@mui/material";
import { useAddMerchantRoleMutation } from "Api/Mutations/Roles/useAddMerchantRoleMutation";
import { AffiliateCodeField } from "Components/Affiliate/AffiliateCodeField/AffiliateCodeField";
import { useAffiliateReferrer } from "Components/Affiliate/AffiliateCodeField/useAffiliateReferrer";
import { useAffiliateCodeVerification } from "Components/Affiliate/AffiliateCodeField/useAffiliateCodeVerification";
import { EULACheckboxLabel } from "Components/Auth/SignUp/EULACheckboxLabel";
import { StatusIndicator } from "Components/Hunter/Merchants/NewRegistration/StatusIndicator";
import { useAvailableAresData } from "Components/Hunter/Merchants/hooks/useAresDataWithAvailability";
import { getAddRoleErrorResource } from "Components/Roles/getAddRoleErrorResource";
import { useSwitchToNewRole } from "Components/Roles/useSwitchToNewRole";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { FormSection } from "Components/Shared/FormSection";
import { CheckboxInput } from "Components/Shared/Inputs/CheckboxInput";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useRegionList } from "Hooks/Options/useRegionList";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import type { Region } from "Infrastructure/Api/Api";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import { removeLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";

const COMPONENT_RESOURCES = Resources.Roles.AddRole;

type FormModel = {
  companyNumber: string;
  region?: Region;
};

export const AddMerchantRoleForm = () => {
  const { t } = useResource();
  const user = useLoggedInUser();
  const { codeListRegions } = useRegionList();
  // Registration is Customer-first, so the merchant terms (formerly agreed on the merchant
  // sign-up) are agreed here — at the moment the role is actually requested.
  const [agreesWithMerchantTerms, setAgreesWithMerchantTerms] = useState(false);

  const { handleSubmit, control, watch, setValue } = useForm<FormModel>({
    // The merchant market is an explicit choice (a licenseless self-registered merchant picks
    // its own region), defaulting to the person's market.
    defaultValues: { companyNumber: "", region: user?.region ?? undefined },
  });
  const companyNumber = watch("companyNumber");
  const region = watch("region");

  const {
    aresData,
    isInvalid,
    isAvailable,
    isLoading: isLoadingCompany,
    reset: resetAresData,
  } = useAvailableAresData(companyNumber, region, AccessRoleCodes.Merchant);

  const handleRegionChange = () => {
    // The register result belongs to the other country's register — start over.
    resetAresData();
    setValue("companyNumber", "");
  };

  // The affiliate code prices the merchant (the recruiting hunter's commission license) right at
  // creation, so it is chosen here, on the first step, not at the end of the wizard.
  const { lockedReferrer, isLoading: isReferrerLoading } = useAffiliateReferrer(
    AccessRoleCodes.Merchant,
  );
  const affiliateCode = useAffiliateCodeVerification({
    role: AccessRoleCodes.Merchant,
    isEnabled: !isReferrerLoading && !lockedReferrer,
  });

  const { switchToRole, isSwitching } = useSwitchToNewRole();

  const { mutate: addMerchantRole, isPending: isAdding } =
    useAddMerchantRoleMutation({
      onSuccess: (result) => {
        toast.success(t(COMPONENT_RESOURCES.Form.Success));
        removeLocalStorageItem(LocalStorageKeys.AffiliateCode);
        switchToRole(result.identityUserPublicID);
      },
      onError: (error) => {
        toast.error(t(getAddRoleErrorResource(error)));
      },
    });

  const onSubmit = () => {
    if (!aresData || !region) {
      return;
    }

    if (affiliateCode.hasUnverifiedCode) {
      affiliateCode.showUnverifiedError();
      return;
    }

    addMerchantRole({
      // The register payload carries no region — stamp the selected market onto the company.
      company: { ...aresData, region },
      affiliateCode: lockedReferrer?.code ?? affiliateCode.verifiedCode,
    });
  };

  const formattedCompanyName =
    (companyNumber.length === 8 && aresData?.companyName) || null;
  const isCompanyAvailable = isAvailable && !!aresData;
  const isLoading = isLoadingCompany || isAdding || isSwitching;
  const isSubmitDisabled =
    isLoading ||
    isReferrerLoading ||
    affiliateCode.fieldProps.isVerifying ||
    !region ||
    !aresData ||
    (!!aresData && !isAvailable) ||
    !agreesWithMerchantTerms;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PrimaryPaper>
        <Stack gap={2}>
          <Typography variant="body2" color="text.secondary">
            {t(COMPONENT_RESOURCES.MerchantForm.Description)}
          </Typography>
          <FormSection>
            <Dropdown
              codeList={codeListRegions}
              control={control}
              name="region"
              label={t(COMPONENT_RESOURCES.Form.Region)}
              onChange={handleRegionChange}
            />
            <FormInput
              name="companyNumber"
              control={control}
              label={t(COMPONENT_RESOURCES.Form.CompanyNumber)}
              disabled={isLoading || !region}
              error={!!aresData && !isAvailable}
            />
            <Box display="flex" alignItems="center">
              <StatusIndicator
                isInvalid={isInvalid}
                company={formattedCompanyName}
                isLoadingCompany={isLoadingCompany}
                isCompanyAvailable={isCompanyAvailable}
              />
            </Box>
          </FormSection>
          <AffiliateCodeField
            lockedReferrer={lockedReferrer}
            isLoading={isReferrerLoading}
            {...affiliateCode.fieldProps}
          />
          <CheckboxInput
            name="agreesWithMerchantTerms"
            label={<EULACheckboxLabel />}
            checked={agreesWithMerchantTerms}
            onChange={() => setAgreesWithMerchantTerms((prev) => !prev)}
          />
          <PrimaryButton
            fullWidth
            type="submit"
            disabled={isSubmitDisabled}
            isLoading={isAdding || isSwitching}
            variant="contained"
            color="primary"
          >
            {t(COMPONENT_RESOURCES.Form.Submit)}
          </PrimaryButton>
        </Stack>
      </PrimaryPaper>
    </form>
  );
};
